/**
 * Words for the Kalshi markets that are not sports (Stuart, 2026-10-27): crypto 15-minute series and the like rode
 * the blotter as a `?` badge and "unlabeled". We never quote them (non_sports_leg); the label only says what the
 * requester bundled.
 */

const MONTHS: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

/** Kalshi's crypto series: the coin, then an optional cadence (`15M`, `D`, `H`, `W`, `MAX`…). */
const CRYPTO_RE = /^KX(BTC|ETH|SOL|XRP|DOGE|BNB|HYPE|NEAR|ADA|AVAX|LTC|LINK|SHIB|SUI|ZEC|TRUMPCOIN)(\d+M|[A-Z]*)?$/;
/** Any coin on Kalshi's 15-minute up/down family (2026-10-27: KXZEC15M was "unlabeled" — the named list cannot keep
 *  up with new coins, and no sport series ends in 15M). */
const CRYPTO_15M_RE = /^KX([A-Z]{2,10})(15M)$/;

/** `KXBTC15M` → `{ coin: 'BTC', cadence: '15m' }`; null when the series is not a crypto one. */
export function cryptoSeriesOf(ticker: string): { coin: string; cadence: string | null } | null {
  const series = ticker.toUpperCase().split('-')[0];
  const m = CRYPTO_RE.exec(series) ?? CRYPTO_15M_RE.exec(series);
  if (!m) return null;
  const tail = m[2] ?? '';
  const cadence = /^\d+M$/.test(tail) ? tail.toLowerCase() : tail === 'D' ? 'daily' : tail === 'H' ? 'hourly' : tail === 'W' ? 'weekly' : tail ? tail.toLowerCase() : null;
  return { coin: m[1], cadence };
}

export function isCryptoTicker(ticker: string | null | undefined): boolean {
  return !!ticker && cryptoSeriesOf(ticker) != null;
}

/** The event segment's date and clock: `26OCT271300` → `10-27 13:00 ET`, `26OCT27` → `10-27`. A trailing team pack
 *  (`26OCT271300ULMLIP`) is read past. */
export function eventWhen(seg: string | undefined): string | null {
  const m = /^\d{2}([A-Z]{3})(\d{2})(?:(\d{2})(\d{2}))?[A-Z]*$/.exec((seg ?? '').toUpperCase());
  const mon = m ? MONTHS[m[1]] : undefined;
  if (!m || !mon) return null;
  return m[3] != null && m[4] != null ? `${mon}-${m[2]} ${m[3]}:${m[4]} ET` : `${mon}-${m[2]}`;
}

/** A threshold strike (`T98000`, `B97500`) — the daily / hourly ladders; a bare 2-digit suffix is not one. */
function cryptoStrikeOf(seg: string | undefined): string | null {
  return seg && /^[TB]\d/.test(seg) ? seg : null;
}

/** A 15-minute window in words (Stuart, 2026-10-27: "BNB 15m › 15" read as a strike). The event segment's clock is the
 *  window's close and the 2-digit suffix its minute — the same minute is redundant (`14:45–15:00 ET`); a different one
 *  names that quarter of the hour (`15:15 ET`). Null when the segment has no clock. */
function windowOf(evSeg: string | undefined, minute: string | undefined, minutes: number): string | null {
  const m = /^\d{2}([A-Z]{3})(\d{2})(\d{2})(\d{2})/.exec((evSeg ?? '').toUpperCase());
  const mon = m ? MONTHS[m[1]] : undefined;
  if (!m || !mon) return null;
  const hh = Number(m[3]);
  const mm = Number(m[4]);
  const day = `${mon}-${m[2]}`;
  if (minute && /^\d{2}$/.test(minute) && Number(minute) !== mm) return `${day} ${m[3]}:${minute} ET`;
  const end = hh * 60 + mm;
  const start = (end - minutes + 1440) % 1440;
  const clock = (x: number) => `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
  return `${day} ${clock(start)}–${clock(end)} ET`;
}

/** A crypto leg's bet and its line (Stuart, 2026-10-27, like a soccer leg's): `BNB 15m up/down` over
 *  `Crypto · 10-27 14:45–15:00 ET`; a `T…` / `B…` suffix is a threshold (`BTC daily › T98000`). Null when not crypto. */
export function cryptoLegTitle(ticker: string, no = false): { bet: string; game: string | null } | null {
  const crypto = cryptoSeriesOf(ticker);
  if (!crypto) return null;
  const parts = ticker.toUpperCase().split('-');
  const strike = cryptoStrikeOf(parts[2]);
  const head = crypto.cadence ? `${crypto.coin} ${crypto.cadence}` : crypto.coin;
  const bet = `${head} ${strike ? `› ${strike}` : 'up/down'}${no ? ' (NO)' : ''}`;
  const span = crypto.cadence && /^\d+m$/.test(crypto.cadence) ? Number(crypto.cadence.slice(0, -1)) : null;
  const when = (span ? windowOf(parts[1], parts[2], span) : null) ?? eventWhen(parts[1]);
  return { bet, game: ['Crypto', when].filter(Boolean).join(' · ') };
}

/** The party a politics ticker's last segment names. */
const PARTY: Record<string, string> = { D: 'Democrats', R: 'Republicans' };
/** Kalshi's election series (Stuart, 2026-10-27: CONTROLS-2026-D, SENATEAK-26-D rode the blotter as "?"): control of
 *  a chamber, a state's Senate / House / Governor race; a trailing S on a Senate race is a special election. */
const POLITICS_RE = /^(?:KX)?(CONTROL([SH])|SENATE([A-Z]{2})(S?)|HOUSE([A-Z]{2})\d*|GOV([A-Z]{2}))-(\d{2,4})-([DR])$/;

export function isPoliticsTicker(ticker: string | null | undefined): boolean {
  return !!ticker && POLITICS_RE.test(ticker.toUpperCase());
}

/** `Democrats win AK Senate` over `Politics · 2026`; NO reads "don't win" — never the other party, since a third-party
 *  winner settles NO for both. Null when not an election ticker. */
export function politicsLegTitle(ticker: string, no = false): { bet: string; game: string | null } | null {
  const m = POLITICS_RE.exec(ticker.toUpperCase());
  if (!m) return null;
  const party = PARTY[m[8]] ?? m[8];
  const race = m[2] ? `the ${m[2] === 'S' ? 'Senate' : 'House'}` : m[3] ? `${m[3]} Senate${m[4] ? ' (special)' : ''}` : m[5] ? `${m[5]} House` : `${m[6]} Governor`;
  const year = m[7].length === 2 ? `20${m[7]}` : m[7];
  return { bet: `${party} ${no ? 'don’t win' : 'win'} ${race}`, game: `Politics · ${year}` };
}

/**
 * `BTC 15m · 10-27 13:00 ET` for a crypto leg; `HIGHNY · 10-27 · T75` for another non-sport series whose event
 * segment carries a date. Null when the ticker has neither shape (a malformed one stays "unlabeled").
 */
export function nonSportLabel(ticker: string): string | null {
  const parts = ticker.toUpperCase().split('-');
  const when = eventWhen(parts[1]);
  const strike = parts[2] && parts[2] !== '00' ? parts.slice(2).join('-') : null;
  const politics = politicsLegTitle(ticker);
  if (politics) return `${politics.bet} · ${politics.game}`;
  const crypto = cryptoLegTitle(ticker);
  if (crypto) return [crypto.bet, crypto.game?.replace(/^Crypto · /, '')].filter(Boolean).join(' · ');
  if (!when) return null;
  const series = parts[0].replace(/^KX/, '');
  return [series, when, strike].filter(Boolean).join(' · ');
}
